"use client";

import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import useOrder from "../hooks/useOrder";
import { useCloud } from "../hooks/useCloud";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPrint, faEdit, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import {
  Filter,
  X,
  Search,
  RefreshCcwDot,
  ChevronDown,
  ChevronUp,
  ShoppingBag,
  IndianRupee,
  TrendingUp,
  CheckCircle,
  Calendar,
  Clock,
  XCircle,
  AlertCircle,
} from "lucide-react";
import StatsBar from "../components/StatsBar";
import Dropdown from "../components/Dropdown";
import PaymentStatusGroup from "../components/PaymentStatusGroup";
import OrderCard from "../components/OrderCard";
import { Reprint } from "../components/Print/ReprintBill";
import { SUCCESS } from "../assets/constants";
import store from "../store";
import toast from "react-hot-toast";
import OrderBiller from "../components/OrderBiller";
import { usePrinters } from "../hooks/usePrinters";
import { DataTable } from "../components/DataTable";
import BillDetailsModal from "../components/BillDetailedView";
import TableSkeleton from "../components/TableLoadingSkeleton";
import SendEbillDrawer from "../components/SendEbillDrawer";

const getOrderStatus = (status) => {
  switch (status) {
    case "settle_later":
      return "Pending";
    case "pending":
      return "Live";
    case "paid":
      return "Paid";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
};

const getOredrStatusColor = (status) => {
  switch (status) {
    case "settle_later":
      return "bg-yellow-100 text-yellow-800 cursor-pointer";
    case "pending":
      return "bg-[#FFA500]  text-white";
    case "cancelled":
      return "bg-red-100 text-red-800";
    default:
      return "bg-green-100 text-green-800";
  }
};

const Orders = ({ onSort = () => {} }) => {
  const { printers } = usePrinters();
  const PASSWORD = store.restaurantInfo.masterPassword;
  const {
    getCurrentDayOrders,
    changePaymentMethod,
    getOrderDetails,
    syncCloud,
    reports,
    settleOrder,
    generateEbillPdf,
  } = useCloud();
  const { getAllCompletedOrders } = useOrder();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tablesVPAMapping] = useState(
    store.tables.reduce((acc, table) => {
      acc[table.qrpidVPA] = `${table.room} T${table.tableNo}`;
      return acc;
    }, {})
  );
  const [staffMapping] = useState(
    store.staff.reduce((acc, staff) => {
      acc[staff.id] = staff.name;
      return acc;
    }, {})
  );

  const [pageWidth, setPageWidth] = useState(0);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isViewBillOpen, setIsViewBillOpen] = useState(false);
  const [viewBillSelectedItem, setViewBillSelectedItem] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [error, setError] = useState("");
  const [isCancleOrder, setIsCancleOrder] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(1);
  const [selectedBillerOrder, setSelectedBillerOrder] = useState(null);
  const [isOrderBillerOpen, setIsOrderBillerOpen] = useState(false);
  const [expandedAmountCell, setExpandedAmountCell] = useState(null);
  const [isSendEbillDrawerOpen, setIsSendEbillDrawerOpen] = useState(false);
  const [selectedOrderForEbill, setSelectedOrderForEbill] = useState(null);
  const [staffIdMap] = useState(() => {
    return store.staff.reduce((acc, staff) => {
      acc[staff.id] = staff;
      return acc;
    }, {});
  });
  const ordersPageRef = useRef(null);

  // Get current page from URL params
  const currentPage = parseInt(searchParams.get("page")) || 1;

  // Function to update page in URL
  const updatePage = (newPage) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", newPage.toString());
    setSearchParams(newSearchParams);
  };

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    paymentMethods: [],
    statuses: [],
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Pagination states
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [itemsPerPage] = useState(10); // Assuming 10 items per page

  // Handle opening the modal for cancellation
  const handleOpenCancelModal = (order) => {
    setSelectedOrder(order);
    setIsCancleOrder(true);
    setError("");
  };

  // Handle closing the modal for cancellation
  const handleCloseModal = () => {
    setIsCancleOrder(false);
    setPaymentMethod("");
    setError("");
  };

  // Handle form submission (Cancel order)
  const handleChangeMethod = async () => {
    if (!paymentMethod) {
      setError("Select Payment Method.");
      return;
    }
    try {
      const billSettlePayload = {
        orderId: selectedOrder["orderId"],
        tableVPA: selectedOrder["tableVPA"],
        paymentPending: false,
        billTxnLogs: {
          ...selectedOrder.billTxnLogs,
          amount: parseInt(selectedOrder.amount),
          paymentType: paymentMethod,
        },
        txnLogs: [
          {
            paymentMethodName: paymentMethod,
            amount: parseFloat(selectedOrder.amount),
          },
        ],
      };

      const { status, data } = await settleOrder(billSettlePayload);

      if (status === SUCCESS) {
        toast.success("Order settled successfully");
        setForceUpdate((prev) => prev + 1);
        handleCloseModal();
      } else {
        toast.error("Something went wrong");
      }
    } catch (error) {
      setError("An error occurred. Please try again.");
    }
  };

  // Available payment methods and statuses for filtering
  const availablePaymentMethods = ["cash", "online", "card"];
  const availableStatuses = ["Paid", "Live", "Cancelled", "Pending"];

  // Toggle status filter
  const toggleStatusFilter = (status) => {
    setActiveFilters((prev) => {
      const statuses = prev.statuses.includes(status)
        ? prev.statuses.filter((s) => s !== status)
        : [...prev.statuses, status];

      return { ...prev, statuses };
    });
  };

  // Toggle payment method filter
  const togglePaymentMethodFilter = (method) => {
    setActiveFilters((prev) => {
      const methods = prev.paymentMethods.includes(method)
        ? prev.paymentMethods.filter((m) => m !== method)
        : [...prev.paymentMethods, method];

      return { ...prev, paymentMethods: methods };
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setActiveFilters({
      paymentMethods: [],
      statuses: [],
    });
  };

  // Handle bill view
  // Handle view bill
  const handleBillView = async (order) => {
    const { data, status } = await getOrderDetails(order.orderId);
    if (status === SUCCESS) {
      order = { ...order, ...data };
      setViewBillSelectedItem(order);
      setIsViewBillOpen(true);
    } else {
      toast.error("Something went wrong please try again ");
    }
  };
  // Handle printing
  const handleBillPrint = async (order) => {
    const { data, status } = await getOrderDetails(order.orderId);
    if (status === SUCCESS) {
      order = { ...order, ...data };
      let tableDetails = store.rooms.reduce(
        (acc, room) => {
          const table = room.tables.find(
            (table) => table.qrpidVPA == order.tableVPA
          );
          if (table) {
            return {
              roomName: room.roomName,
              tableNo: table.tableNo,
            };
          }
          return acc;
        },
        {
          roomName: "",
          tableNo: "",
        }
      );
      await Reprint(
        {
          ...order,
          billStatus: getOrderStatus(order.billStatus),
          tableDetails: tableDetails,
        },
        printers
      );
    } else {
      toast.error("Something went wrong please try again ");
    }
  };

  const handleBillUpdate = async (order) => {
    const { data, status } = await getOrderDetails(order.orderId);
    if (status === SUCCESS) {
      order = { ...order, ...data };
      setSelectedBillerOrder(order);
      setIsOrderBillerOpen(true);
    } else {
      toast.error("Something went wrong please try again ");
    }
  };

  const handleSendEbill = async (orderId, customerName, phoneNumber) => {
    try {
      const { status, data, message } = await generateEbillPdf(orderId, customerName, phoneNumber);
      if (status === "success") {
        toast.success(`E-Bill sent successfully to ${customerName}`);
        return { status: "success", data };
      } else {
        throw new Error(message || "Failed to generate e-bill");
      }
    } catch (error) {
      console.error("Error sending e-bill:", error);
      throw new Error(error.message || "Failed to send e-bill. Please try again.");
    }
  };

  const handleOpenSendEbillDrawer = (order) => {
    setSelectedOrderForEbill(order);
    setIsSendEbillDrawerOpen(true);
  };

  const fetchReports = async () => {
    setLoading(true);
    const reports_data = await reports({
      dateType: "today",
      page: currentPage,
    });

    if (reports_data && reports_data.status === SUCCESS) {
      const {
        data: { orders, totalAmountInRange, totalAmountTillNow },
        pagination,
      } = reports_data;

      // Set pagination data
      if (pagination) {
        const { totalPages, totalItems } = pagination;
        setTotalPages(parseInt(totalPages));
        setTotalOrders(parseInt(totalItems));
      }

      setOrders(orders);
      setFilteredOrders(orders);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [currentPage, forceUpdate]);

  useEffect(() => {
    let result = orders;

    // Apply search term
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(
        (order) =>
          order.billNo?.toString().includes(lowercasedSearch) ||
          tablesVPAMapping[order.tableVPA]
            ?.toLowerCase()
            .includes(lowercasedSearch) ||
          order.paymentmethod?.toLowerCase().includes(lowercasedSearch) ||
          order.billStatus?.toLowerCase().includes(lowercasedSearch) ||
          new Date(order.createdAt)
            .toLocaleString()
            .toLowerCase()
            .includes(lowercasedSearch)
      );
    }

    // Apply payment method filters
    if (activeFilters.paymentMethods.length > 0) {
      result = result.filter((order) =>
        activeFilters.paymentMethods.includes(
          order.paymentmethod?.toLowerCase()
        )
      );
    }

    // Apply status filters
    if (activeFilters.statuses.length > 0) {
      result = result.filter((order) =>
        activeFilters.statuses.includes(getOrderStatus(order.billStatus))
      );
    }

    setFilteredOrders(result);
  }, [searchTerm, activeFilters, orders]);

  useEffect(() => {
    if (!ordersPageRef.current) return;

    // Set initial width
    setPageWidth(ordersPageRef.current.clientWidth + "px");

    // Create ResizeObserver to watch for width changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        console.log("Width changed to:", width);
        setPageWidth(width + "px");
      }
    });

    // Start observing the element
    resizeObserver.observe(ordersPageRef.current);

    // Cleanup function
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const formateDate = (time) => {
    const orderTime = new Date(time);
    const day = String(orderTime.getDate()).padStart(2, "0");
    const month = String(orderTime.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed
    const year = orderTime.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;
    return formattedDate;
  };

  const updateOrder = async (orderId) => {
    const { data, status } = await getOrderDetails(orderId);
    if (status === SUCCESS) {
      setOrders(orders.map((o) => (o.orderId === data.orderId ? data : o)));
      setFilteredOrders(
        filteredOrders.map((o) => (o.orderId === data.orderId ? data : o))
      );
      setIsOrderBillerOpen(false);
    } else {
      toast.error("Something went wrong please try again ");
    }
  };

  // Calculate summary metrics
  const calculateSummaryMetrics = () => {
    const totalOrders = filteredOrders.length;
    const totalRevenue = filteredOrders.reduce(
      (sum, order) => sum + Number(order.billTxnLogs.total),
      0
    );
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    const paidOrders = filteredOrders.filter(
      (order) => order.billStatus === "paid"
    ).length;
    const paidPercentage =
      totalOrders > 0 ? (paidOrders / totalOrders) * 100 : 0;

    return {
      totalOrders,
      totalRevenue: totalRevenue.toFixed(2),
      avgOrderValue: avgOrderValue.toFixed(2),
      paidPercentage: paidPercentage.toFixed(1),
    };
  };

  const metrics = calculateSummaryMetrics();

  // Calculate order stats for StatsBar
  const calculateOrderStats = () => {
    const liveOrders = filteredOrders.filter(
      (order) => order.billStatus === "pending"
    ).length;
    const paidOrders = filteredOrders.filter(
      (order) => order.billStatus === "paid"
    ).length;
    const pendingOrders = filteredOrders.filter(
      (order) => order.billStatus === "settle_later"
    ).length;
    const cancelledOrders = filteredOrders.filter(
      (order) => order.billStatus === "cancelled"
    ).length;

    return [
      {
        id: "live",
        icon: Clock,
        iconBg: "#FFF7ED",
        iconColor: "#EA580C",
        value: liveOrders,
        label: "Live Orders",
      },
      {
        id: "paid",
        icon: CheckCircle,
        iconBg: "#ECFDF5",
        iconColor: "#059669",
        value: paidOrders,
        label: "Paid Orders",
      },
      {
        id: "pending",
        icon: AlertCircle,
        iconBg: "#FEFCE8",
        iconColor: "#CA8A04",
        value: pendingOrders,
        label: "Pending Payment",
      },
      {
        id: "cancelled",
        icon: XCircle,
        iconBg: "#FEF2F2",
        iconColor: "#DC2626",
        value: cancelledOrders,
        label: "Cancelled",
      },
    ];
  };

  return (
    <div
      className="space-y-2 p-4 md:p-6 bg-gray-50 h-screen overflow-hidden overflow-y-auto hide-scrollbar"
      ref={ordersPageRef}
    >
      <div className="flex flex-col space-y-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-2 md:space-y-0">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              Today's Orders
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage and track today's order activity
            </p>
          </div>

          <StatsBar
            stats={calculateOrderStats()}
            isLoading={loading}
            onStatClick={(id) => {
              const statusMap = {
                live: "Live",
                paid: "Paid",
                pending: "Pending",
                cancelled: "Cancelled",
              };
              toggleStatusFilter(statusMap[id]);
            }}
          />
        </div>

        {/* Search and Filters - Mobile Responsive */}
        <div className="flex flex-col md:flex-row md:justify-end md:items-center space-y-3 md:space-y-0 md:space-x-3">
          {/* Search bar */}
          <div className="relative w-full md:w-auto">
            <div className="absolute left-0 pl-3 h-9 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search orders..."
              className="block w-full md:w-64 pl-10 pr-3 h-9 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          {/* Filter and Refresh Buttons */}
          <div className="flex items-center space-x-2 md:space-x-3">
            {/* Filter Button */}
            <Dropdown
              isOpen={isFilterOpen}
              onToggle={setIsFilterOpen}
              placement="bottom-end"
              dropdownClassName="w-72 p-4"
              trigger={
                <button
                  className={`inline-flex items-center px-3 h-9 border rounded-md text-sm font-medium transition-colors ${
                    isFilterOpen ||
                    activeFilters.paymentMethods.length > 0 ||
                    activeFilters.statuses.length > 0
                      ? "bg-blue-50 text-blue-700 border-blue-300"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  <span className="hidden md:inline">Filters</span>
                  {(activeFilters.paymentMethods.length > 0 ||
                    activeFilters.statuses.length > 0) && (
                    <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {activeFilters.paymentMethods.length +
                        activeFilters.statuses.length}
                    </span>
                  )}
                </button>
              }
            >
              <div className="space-y-4">
                {/* Payment Method Filters */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <IndianRupee className="h-4 w-4 mr-2 text-gray-500" />
                    Payment Method
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {availablePaymentMethods.map((method) => (
                      <button
                        key={method}
                        onClick={() => togglePaymentMethodFilter(method)}
                        className={`px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-center ${
                          activeFilters.paymentMethods.includes(method)
                            ? "bg-blue-50 text-blue-700 border-blue-200 border"
                            : "bg-gray-50 text-gray-700 border-gray-300 border hover:bg-gray-100"
                        }`}
                      >
                        {method.charAt(0).toUpperCase() + method.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Order Status Filters */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2 text-gray-500" />
                    Order Status
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {availableStatuses.map((status) => (
                      <button
                        key={status}
                        onClick={() => toggleStatusFilter(status)}
                        className={`px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-center ${
                          activeFilters.statuses.includes(status)
                            ? "bg-blue-50 text-blue-700 border-blue-200 border"
                            : "bg-gray-50 text-gray-700 border-gray-300 border hover:bg-gray-100"
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Dropdown>

            {/* Clear Filters */}
            {(searchTerm ||
              activeFilters.paymentMethods.length > 0 ||
              activeFilters.statuses.length > 0) && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-3 h-9 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <X className="h-4 w-4 mr-2" />
                <span className="hidden md:inline">Clear</span>
              </button>
            )}

            {/* Refresh Button */}
            <button
              onClick={fetchReports}
              className="inline-flex items-center px-3 h-9 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              title="Refresh"
            >
              <RefreshCcwDot className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          <TableSkeleton rows={5} columns={10} hasHeader={true} />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-4">
            {filteredOrders
              .sort((a, b) => {
                const dateDiff = new Date(b.createdAt) - new Date(a.createdAt);
                if (dateDiff !== 0) return dateDiff;
                return a.billNo - b.billNo;
              })
              .map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onView={handleBillView}
                  onPrint={handleBillPrint}
                  onEdit={handleBillUpdate}
                  onSendEbill={handleOpenSendEbillDrawer}
                  tablesVPAMapping={tablesVPAMapping}
                  staffIdMap={staffIdMap}
                  formateDate={formateDate}
                />
              ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block" data-testid="orders-table-container">
            <DataTable
              width={pageWidth}
              data={filteredOrders.sort((a, b) => {
                const dateDiff = new Date(b.createdAt) - new Date(a.createdAt);
                if (dateDiff !== 0) return dateDiff;
                return a.billNo - b.billNo;
              })}
              className="bg-white rounded-lg shadow-sm"
              columns={[
                {
                  key: "orderInfo",
                  label: "Order Info",
                  width: "150px",
                  render: (order) => (
                    <div
                      className="space-y-1"
                      style={{
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      <div className="font-semibold text-gray-900" data-testid={`order-bill-no-${order.billNo}`}>
                        Bill #{order.billNo}
                      </div>
                      <div className="text-sm text-gray-600">
                        {order.typeOfOrder === "Takeaway"
                          ? "Parcel"
                          : `${tablesVPAMapping[order.tableVPA] || "-"}`}
                      </div>
                      {/* Staff Section */}
                      {staffIdMap[order.staffId]?.name && (
                        <div className="text-xs text-gray-500">
                          <span className="font-medium text-blue-600">
                            Staff:
                          </span>{" "}
                          {staffIdMap[order.staffId].name}
                        </div>
                      )}

                      {/* Tip Section */}
                      {order.billTxnLogs.tip > 0 && (
                        <div className="text-xs text-gray-500">
                          <span className="font-medium text-green-600">
                            Tip:
                          </span>{" "}
                          ₹{order.billTxnLogs.tip}
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: "amountInfo",
                  label: "Amount",
                  width: "30%",
                  align: "end",
                  render: (order) => {
                    const isExpanded = expandedAmountCell === order.orderId;

                    return (
                      <div
                        className="cursor-pointer transition-all duration-300 ease-in-out hover:bg-gray-50 rounded p-1"
                        onClick={() => {
                          setExpandedAmountCell(
                            isExpanded ? null : order.orderId
                          );
                        }}
                      >
                        {/* Total Amount - Always Visible */}
                        <div className="flex items-center justify-end gap-2">
                          {/* Coupon Tag */}
                          {order.couponMetadata && order.couponMetadata !== "NA" && (
                            <div className="bg-green-50 text-green-700 text-xs font-medium px-2 py-1 rounded-full border border-green-200 flex items-center">
                              <span className="mr-1">🏷️</span>
                              Offer Applied
                            </div>
                          )}
                          {/* Amount */}
                          <div className="text-lg font-bold text-gray-900 flex items-center" data-testid={`order-amount-${order.billNo}`}>
                            ₹
                            {Number.parseFloat(order.billTxnLogs.total).toFixed(
                              2
                            )}
                            <span>
                              {" "}
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 ml-2" />
                              ) : (
                                <ChevronDown className="w-4 h-4 ml-2" />
                              )}{" "}
                            </span>
                          </div>
                        </div>

                        {/* Breakdown - Expandable with smooth transition */}
                        <div
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            isExpanded
                              ? "max-h-96 opacity-100"
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          <div className="mt-2 bg-gray-100 rounded p-2 space-y-1 text-xs">
                            {/* Subtotal */}
                            <div className="flex justify-between text-gray-700">
                              <span>Subtotal:</span>
                              <span>
                                ₹
                                {Number.parseFloat(
                                  order.billTxnLogs.subTotal
                                ).toFixed(2)}
                              </span>
                            </div>

                            {/* Discount */}
                            {order.billTxnLogs.discount > 0 && (
                              <>
                                <div className="flex justify-between text-green-600">
                                  <span>Discount:</span>
                                  <span>
                                    -₹
                                    {Number.parseFloat(
                                      order.billTxnLogs.discount
                                    ).toFixed(2)}
                                  </span>
                                </div>
                                {/* Coupon Details */}
                                {order.couponMetadata && order.couponMetadata !== "NA" && (
                                  <div className="flex flex-col gap-0.5 pl-4">
                                    <div className="flex justify-between text-xs text-gray-500">
                                      <span>
                                        Coupon ({order.couponMetadata.couponCode}):
                                      </span>
                                      <span>
                                        {order.couponMetadata.couponValue}% off
                                      </span>
                                    </div>
                                    <div className="flex justify-between text-xs text-green-600">
                                      <span>Coupon Value:</span>
                                      <span>
                                        -₹{(order.billTxnLogs.subTotal * (order.couponMetadata.couponValue / 100)).toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}

                            {/* CGST */}
                            { order.billTxnLogs.gst && order.billTxnLogs.cgst > 0 && (
                              <div className="flex justify-between text-purple-600">
                                <span>
                                  CGST ({order.billTxnLogs.cgstPercent}%):
                                </span>
                                <span>
                                  ₹
                                  {Number.parseFloat(
                                    order.billTxnLogs.cgst
                                  ).toFixed(2)}
                                </span>
                              </div>
                            )}

                            {/* SGST */}
                            { order.billTxnLogs.gst && order.billTxnLogs.sgst > 0 && (
                              <div className="flex justify-between text-purple-600">
                                <span>
                                  SGST ({order.billTxnLogs.sgstPercent}%):
                                </span>
                                <span>
                                  ₹
                                  {Number.parseFloat(
                                    order.billTxnLogs.sgst
                                  ).toFixed(2)}
                                </span>
                              </div>
                            )}

                            {/* Service Charges */}
                            {order.billTxnLogs.service && order.billTxnLogs.serviceAmount > 0 && (
                              <div className="flex justify-between text-purple-600">
                                <span>
                                  Service Charge ({order.billTxnLogs.servicePercentage}%):
                                </span>
                                <span>
                                  ₹
                                  {Number.parseFloat(
                                    order.billTxnLogs.serviceAmount
                                  ).toFixed(2)}
                                </span>
                              </div>
                            )}

                            {/* Coupon Details */}
                            {order.couponMetadata && order.couponMetadata !== "NA" && (
                              <div className="flex justify-between text-green-600">
                                <span>Coupon:</span>
                                <span>- ₹{order.couponMetadata.couponValue}</span>
                              </div>
                            )}

                            {/* Tip */}
                            {order.billTxnLogs.tip > 0 && (
                              <div className="flex justify-between text-blue-600">
                                <span>Tip:</span>
                                <span>₹{order.billTxnLogs.tip}</span>
                              </div>
                            )}
                            
                            {/* Final Total */}
                            <div className="pt-1 border-t border-gray-300">
                              <div className="flex justify-between font-semibold text-gray-900">
                                <span>Final Total:</span>
                                <span>
                                  ₹
                                  {Number.parseFloat(
                                    order.billTxnLogs.total
                                  ).toFixed(2)}
                                </span>
                              </div>
                            </div>

                            {/* Payment Split Details */}
                            {order.paymentmethod === "partial" &&
                              order?.partialAmoutsplit && (
                                <div className="pt-1 border-t border-gray-300">
                                  <div className="text-gray-600 font-medium mb-1">
                                    Payment Split:
                                  </div>
                                  {order.partialAmoutsplit.map(
                                    (ele, idx) =>
                                      ele.amount > 0 && (
                                        <div
                                          key={idx}
                                          className="flex justify-between text-gray-700"
                                        >
                                          <span>{ele.name}:</span>
                                          <span>₹{ele.amount}</span>
                                        </div>
                                      )
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    );
                  },
                },
                {
                  key: "paymentStatus",
                  label: "Payment & Status",
                  width: "25%",
                  align: "center",
                  render: (order) => (
                    <div className="flex justify-center items-center w-full">
                      <PaymentStatusGroup
                        paymentMethod={order.paymentmethod}
                        status={order.billStatus}
                        isCanceled={order.canceled}
                        orderType={order.orderType}
                        onClick={() => {
                          if (order.billStatus === "settle_later") {
                            handleOpenCancelModal(order);
                          }
                        }}
                      />
                    </div>
                  ),
                },
                {
                  key: "timeInfo",
                  label: "Time",
                  width: "15%",
                  align: "center",
                  render: (order) => (
                    <div className="flex flex-col items-center justify-center space-y-1">
                      <div className="text-sm font-medium text-gray-900">
                        {new Date(order.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formateDate(order.createdAt)}
                      </div>
                    </div>
                  ),
                },
                {
                  key: "actions",
                  label: "Actions",
                  width: "10%",
                  align: "center",
                  render: (order) => (
                    <div className="flex items-center justify-center space-x-1">
                      <button
                        onClick={() => handleBillView(order)}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                        title="View Details"
                      >
                        <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                      </button>
                      {!order.canceled && (
                        <button
                          onClick={() => handleBillPrint(order)}
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors duration-200"
                          title="Print Bill"
                        >
                          <FontAwesomeIcon icon={faPrint} className="w-4 h-4" />
                        </button>
                      )}
                      {!order.canceled && (
                        <button
                          onClick={() => handleBillUpdate(order)}
                          className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-md transition-colors duration-200"
                          title="Edit Order"
                        >
                          <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleOpenSendEbillDrawer(order)}
                        className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors duration-200"
                        title="Send E-Bill"
                      >
                        <FontAwesomeIcon icon={faPaperPlane} className="w-4 h-4" />
                      </button>
                    </div>
                  ),
                },
              ]}
              title="Today's Orders"
              subtitle={`${filteredOrders.length} Orders`}
              sortableKeys={[
                "orderInfo",
                "amountInfo",
                "paymentStatus",
                "timeInfo",
              ]}
              onSort={(key, direction) => {
                // Handle sorting logic here
                console.log(`Sorting by ${key} in ${direction} direction`);
              }}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={updatePage}
              onNext={() => updatePage(Math.min(currentPage + 1, totalPages))}
              onPrev={() => updatePage(Math.max(currentPage - 1, 1))}
              showHeader={false}
            />
          </div>
        </div>
      )}

      {/* Change Payment Method Modal */}
      {isCancleOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Change Payment Method
            </h3>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
                {error}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => {
                  setPaymentMethod(e.target.value);
                  setError("");
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="">Select Payment Method</option>
                <option value="cash">Cash</option>
                <option value="online">Online</option>
                <option value="card">Card</option>
              </select>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={handleChangeMethod}
              >
                Change Method
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Bill Modal */}
      {isViewBillOpen && viewBillSelectedItem && (
        <BillDetailsModal
          selectedBill={viewBillSelectedItem}
          setIsViewBillOpen={setIsViewBillOpen}
        />
      )}

      {/* Order Biller */}
      {isOrderBillerOpen && (
        <OrderBiller
          isOpen={isOrderBillerOpen}
          onClose={() => setIsOrderBillerOpen(false)}
          onUpdate={updateOrder}
          order={selectedBillerOrder}
        />
      )}

      {/* Send E-Bill Drawer */}
      {isSendEbillDrawerOpen && selectedOrderForEbill && (
        <SendEbillDrawer
          isOpen={isSendEbillDrawerOpen}
          onClose={() => {
            setIsSendEbillDrawerOpen(false);
            setSelectedOrderForEbill(null);
          }}
          orderId={selectedOrderForEbill.orderId}
          onSend={handleSendEbill}
        />
      )}
    </div>
  );
};

export default Orders; 
